/**
 * JSON-LD Structured Data Generators for SEO
 *
 * Generates schema.org structured data for Living Project Guide pages.
 * These schemas enable Google rich results (HowTo snippets, FAQ accordions).
 *
 * IMPORTANT: Structured data reflects template defaults, NOT personalized values.
 * Google crawls server-rendered defaults; users see personalized values after interaction.
 */

import { getCategoryLabel } from './constants'
import type { ProjectTemplate } from '../types'
import { getSiteUrl } from '@/utils/site-url'

const BASE_URL = getSiteUrl()

interface HowToStep {
  '@type': 'HowToStep'
  position: number
  name: string
  text: string
  image?: string
}

interface HowToTool {
  '@type': 'HowToTool'
  name: string
}

interface HowToSupply {
  '@type': 'HowToSupply'
  name: string
  requiredQuantity?: string
}

interface HowToSchema {
  '@context': 'https://schema.org'
  '@type': 'HowTo'
  name: string
  description: string
  mainEntityOfPage?: string
  image?: string
  totalTime?: string
  estimatedCost?: {
    '@type': 'MonetaryAmount'
    currency: 'USD'
    minValue?: number
    maxValue?: number
  }
  tool?: Array<HowToTool>
  supply?: Array<HowToSupply>
  step: Array<HowToStep>
}

interface FAQItem {
  '@type': 'Question'
  name: string
  acceptedAnswer: {
    '@type': 'Answer'
    text: string
  }
}

interface FAQPageSchema {
  '@context': 'https://schema.org'
  '@type': 'FAQPage'
  mainEntity: Array<FAQItem>
}

interface BreadcrumbItem {
  '@type': 'ListItem'
  position: number
  name: string
  item: string
}

interface BreadcrumbListSchema {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: Array<BreadcrumbItem>
}

/**
 * Convert hours to ISO 8601 duration format (PT#H#M)
 */
function hoursToISO8601(hours?: number): string | undefined {
  if (hours === undefined || hours < 0) return undefined
  if (hours === 0) return 'PT0H'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  if (minutes === 0) {
    return `PT${wholeHours}H`
  }
  return `PT${wholeHours}H${minutes}M`
}

/**
 * Generate HowTo schema for Google rich results
 */
export function generateHowToSchema(
  template: ProjectTemplate,
  canonicalUrl: string
): HowToSchema {
  const schema: HowToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: template.title,
    description: template.description,
    mainEntityOfPage: canonicalUrl,
    step: template.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.description,
    })),
  }

  if (template.heroImageUrl) {
    schema.image = template.heroImageUrl
  }

  if (template.estimatedHours) {
    schema.totalTime = hoursToISO8601(template.estimatedHours)
  }

  if (template.estimatedCostMin || template.estimatedCostMax) {
    schema.estimatedCost = {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      minValue: template.estimatedCostMin,
      maxValue: template.estimatedCostMax,
    }
  }

  // Aggregate tools from all steps
  const toolsSet = new Set<string>()
  for (const step of template.steps) {
    for (const tool of step.tools ?? []) {
      const name = tool.name ?? tool.customName
      if (name) toolsSet.add(name)
    }
  }
  if (toolsSet.size > 0) {
    schema.tool = Array.from(toolsSet).map((name) => ({
      '@type': 'HowToTool',
      name,
    }))
  }

  // Aggregate materials from all steps
  const materialsMap = new Map<string, { quantity?: number; unit?: string }>()
  for (const step of template.steps) {
    for (const material of step.materials ?? []) {
      const name = material.name ?? material.customName
      if (name && !materialsMap.has(name)) {
        materialsMap.set(name, {
          quantity: material.quantity,
          unit: material.unit,
        })
      }
    }
  }
  if (materialsMap.size > 0) {
    schema.supply = Array.from(materialsMap.entries()).map(([name, { quantity, unit }]) => {
      const supply: HowToSupply = {
        '@type': 'HowToSupply',
        name,
      }
      if (quantity && unit) {
        supply.requiredQuantity = `${quantity} ${unit}`
      }
      return supply
    })
  }

  return schema
}

/**
 * Generate FAQPage schema for Google FAQ rich results
 */
export function generateFAQSchema(
  faqItems: Array<{ question: string; answer: string }>
): FAQPageSchema | null {
  if (faqItems.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

/**
 * Generate BreadcrumbList schema for navigation context
 */
export function generateBreadcrumbSchema(
  template: ProjectTemplate,
  canonicalUrl: string
): BreadcrumbListSchema {
  const categoryLabel = getCategoryLabel(template.category)

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'DIY Projects',
        item: `${BASE_URL}/diy`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: categoryLabel,
        item: `${BASE_URL}/diy?category=${template.category}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: template.title,
        item: canonicalUrl,
      },
    ],
  }
}

/**
 * Build canonical URL for a template
 */
export function buildCanonicalUrl(template: ProjectTemplate): string {
  const slug = template.canonicalSlug ?? template.slug
  return `${BASE_URL}/diy/${slug}`
}
